import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { History, Search, SlidersHorizontal, AlertCircle, RefreshCw } from 'lucide-react';

const RISK_BADGE = {
  High:   'bg-red-500/15 text-red-400 border border-red-500/25',
  Medium: 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/25',
  Low:    'bg-green-500/15 text-green-400 border border-green-500/25',
};

const RISK_BAR = { High: 'bg-red-500', Medium: 'bg-yellow-400', Low: 'bg-green-500' };
const FILTERS  = ['All', 'Low', 'Medium', 'High'];

export default function ScanHistory() {
  const { token, API_URL } = useAuth();
  const [scans,   setScans]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [filter,  setFilter]  = useState('All');
  const [search,  setSearch]  = useState('');

  const fetchScans = () => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    setError('');
    fetch(`${API_URL}/api/my-scans`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async r => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error || `Server error (${r.status})`);
        }
        return r.json();
      })
      .then(data => setScans(Array.isArray(data) ? data : []))
      .catch(err => setError(err.message || 'Failed to load scan history'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchScans(); }, [token, API_URL]); // eslint-disable-line

  const visible = scans.filter(s => {
    const riskMatch   = filter === 'All' || s.riskLevel === filter;
    const searchMatch = !search || s.wallet?.toLowerCase().includes(search.toLowerCase());
    return riskMatch && searchMatch;
  });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-white">Scan History</h1>
          <p className="text-slate-500 text-sm mt-0.5">All previous wallet scans on your account</p>
        </div>
        <button
          onClick={fetchScans}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 bg-white/[0.04] hover:bg-white/[0.07] border border-white/[0.07] px-3 py-2 rounded-lg transition-all disabled:opacity-40"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="glass-card p-4 mb-5 border border-red-500/20 flex items-center gap-3">
          <AlertCircle size={15} className="text-red-400 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-red-400 font-medium">Failed to load scan history</p>
            <p className="text-xs text-red-400/70 mt-0.5">{error}</p>
          </div>
          <button
            onClick={fetchScans}
            className="text-xs text-red-400 hover:text-red-300 border border-red-500/25 px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      <div className="glass-card p-5">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
            <input
              type="text"
              placeholder="Search by wallet address…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full bg-white/[0.04] border border-white/[0.07] rounded-xl pl-8 pr-4 py-2 text-sm text-slate-200 placeholder-slate-700 font-mono focus:outline-none focus:border-[#00E5FF]/40 transition-all"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <SlidersHorizontal size={13} className="text-slate-600 mr-1" />
            {FILTERS.map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={[
                  'px-3 py-1.5 rounded-lg text-xs font-medium transition-all border',
                  filter === f
                    ? 'bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/25'
                    : 'text-slate-500 hover:text-slate-300 hover:bg-white/5 border-transparent',
                ].join(' ')}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !error && visible.length === 0 ? (
          <div className="text-center py-14">
            <History size={28} className="text-slate-700 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">
              {scans.length === 0
                ? 'No scans yet. Go to Wallet Scanner to scan your first address.'
                : 'No results match your filter.'}
            </p>
          </div>
        ) : !error ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-600 text-xs border-b border-white/[0.05]">
                  <th className="text-left pb-3 pr-4 font-medium">Wallet Address</th>
                  <th className="text-left pb-3 pr-4 font-medium">Risk Score</th>
                  <th className="text-left pb-3 pr-4 font-medium">Level</th>
                  <th className="text-left pb-3 pr-4 font-medium">Reputation</th>
                  <th className="text-left pb-3 font-medium">Scanned</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {visible.map((scan, i) => (
                  <tr key={scan._id || i} className="hover:bg-white/[0.02] transition-colors group">
                    <td className="py-3 pr-4 font-mono text-xs text-slate-500 group-hover:text-slate-300 transition-colors max-w-[220px] truncate">
                      {scan.wallet}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <div className="h-1 w-16 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${RISK_BAR[scan.riskLevel] || 'bg-slate-500'}`}
                            style={{ width: `${scan.riskScore}%` }}
                          />
                        </div>
                        <span className="text-slate-400 text-xs tabular-nums font-medium">
                          {scan.riskScore}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${RISK_BADGE[scan.riskLevel] || ''}`}>
                        {scan.riskLevel}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-slate-400 text-xs tabular-nums">
                      {scan.reputationScore}/100
                    </td>
                    <td className="py-3 text-slate-600 text-xs tabular-nums whitespace-nowrap">
                      {new Date(scan.date || scan.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {!loading && !error && (
          <div className="mt-4 pt-3 border-t border-white/[0.04] text-xs text-slate-600">
            Showing {visible.length} of {scans.length} scan{scans.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  );
}
